import React from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { useStateValue } from '../context/state.jsx';
import { setShowAddProjectModal } from '../context/datasets.js';
import { closeOpenProject, focusProject, sourceFormatLabel } from '../util/openProject.js';

const ProjectRow = ({
	datasetId,
	dataset,
	isCurrent,
	onOpen,
	onClose
}) => (
	<li className="setupProjectRow">
		<div>
			<div className="setupProjectRowMeta">
				<strong>{dataset.Name}</strong>
				<span className="sourceFormatBadge">{sourceFormatLabel(dataset)}</span>
			</div>
			<div className="setupProjectRowSub">
				{dataset.Owned ? 'Owned by you' : `Shared by ${dataset.Owner || 'another user'}`}
			</div>
		</div>
		<div className="setupProjectRowActions">
			{isCurrent
				? <Button bsSize="small" onClick={onClose}>Close</Button>
				: <Button bsSize="small" bsStyle="primary" onClick={onOpen}>Open</Button>}
			<Link className="btn btn-default btn-sm" to={`/setup/projects/${datasetId}`}>Configure</Link>
		</div>
	</li>
);

ProjectRow.propTypes = {
	datasetId: PropTypes.string.isRequired,
	dataset: PropTypes.object.isRequired,
	isCurrent: PropTypes.bool,
	onOpen: PropTypes.func,
	onClose: PropTypes.func
};

const ProjectSetup = ({ datasetsLoading }) => {
	const [{ datasets, currentDataset }, dispatch] = useStateValue();
	const navigate = useNavigate();
	const datasetMap = datasets && typeof datasets === 'object' && !Array.isArray(datasets) ? datasets : {};
	const datasetIds = Object.keys(datasetMap);
	const orderedIds = [
		...datasetIds.filter(id => String(id) === String(currentDataset)),
		...datasetIds.filter(id => String(id) !== String(currentDataset))
	];

	const openProject = datasetId => {
		focusProject({ dispatch, navigate, datasetId });
	};

	const closeProject = datasetId => {
		closeOpenProject({
			dispatch,
			navigate,
			datasetId,
			focusedDatasetId: currentDataset,
			redirectIfFocused: false
		});
	};

	if (!datasetIds.length) {
		return (
			<div className="paddedTabContent setupProjectsPane">
				<div className="setupSectionHeader">
					<div>
						<h2>Project configuration</h2>
						<p>Add a project, then open it to see the schedule.</p>
					</div>
					<Button
						bsStyle="primary"
						className="setupPrimaryBtn"
						onClick={() => dispatch(setShowAddProjectModal(true))}>
						<span className="glyphicon glyphicon-cloud leftGlyph"></span>
						Add Project
					</Button>
				</div>
				<div className="setupEmptyState">
					<p>
						{datasetsLoading
							? 'Loading projects…'
							: 'No projects are loaded yet. Add a project, then open it from this list.'}
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="paddedTabContent setupProjectsPane">
			<div className="setupSectionHeader">
				<div>
					<h2>Project configuration</h2>
					<p>Opening a project replaces the one that is open. A prior update is a comparison, not a second open project.</p>
				</div>
				<Button
					bsStyle="primary"
					className="setupPrimaryBtn"
					onClick={() => dispatch(setShowAddProjectModal(true))}>
					<span className="glyphicon glyphicon-cloud leftGlyph"></span>
					Add Project
				</Button>
			</div>

			<section className="setupCard setupProjectListCard">
				<div className="setupCardHeader">
					<div>
						<h3>Library</h3>
						<p>Projects you can access. Configure opens settings. Close leaves the schedule.</p>
					</div>
				</div>
				<ul className="setupProjectList">
					{orderedIds.map(datasetId => (
						<ProjectRow
							key={datasetId}
							datasetId={datasetId}
							dataset={datasetMap[datasetId]}
							isCurrent={String(currentDataset) === String(datasetId)}
							onOpen={() => openProject(datasetId)}
							onClose={() => closeProject(datasetId)} />
					))}
				</ul>
			</section>
		</div>
	);
};

ProjectSetup.propTypes = {
	datasetsLoading: PropTypes.bool
};

export default ProjectSetup;
